import { useCallback, useEffect, useRef, useState } from "react";
import { UserDetails} from "./models";
import { useLoader } from "src/contexts/LoaderContext";
import { Avatar, AvatarFallback, AvatarImage } from "../../../components/ui/avatar";
import { Button } from "../../../components/ui/button";
import { Input } from "src/components/ui/input";
import { Upload, Pencil, Trash2, User, UploadIcon } from "lucide-react";
import { classNames } from "src/helpers/global";
import { useAlert } from "src/hooks/use-alert";
import { MyAvatarDto } from "src/models/user.dto";
import UserApi from "src/api/user.api";
import { DeleteConfirmationDialog } from "src/components/ui/alert-dialogs/AlertDialogDestructive";

export const AvatarSection = () => {
    const {showLoader, hideLoader} = useLoader();
    const [avatar, setAvatar] = useState<MyAvatarDto | null>(null);
    const {showWarning, showError} = useAlert();
    const inputRef = useRef<HTMLInputElement>(null);
    const [validPhoto, setValidPhoto] = useState<boolean>(true);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [removePhoto, setRemovePhoto] = useState<boolean>(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [updatingPhoto, setUpdatingPhoto] = useState(false);
    const [photoError, setPhotoError] = useState<string>("");
    const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
    const userApi = UserApi();

    useEffect(() => {
       userApi.GetUserAvatarAsync()
        .then((result) => {
            setAvatar(result);
        })
        .catch(() => {

        })
    }, [])

    const handleUpdatePhotoChanges = async (e : React.ChangeEvent<HTMLInputElement>) => {
        try {
            showLoader();

            const selected = e.target.files?.[0];
            setPhotoError("");

            if (!selected) return;

            if (!["image/jpeg", "image/png", "image/webp"].includes(selected.type)) {
                setPhotoError("Unsupported format.");
                e.target.value = "";
                hideLoader();
                return;
            }

            if (selected.size > 5 * 1024 * 1024) {
                setPhotoError("File too large.");
                e.target.value = "";
                hideLoader();
                return;
            }
            await userApi.UpdateAvatarAsync(selected);
            const updatedAvatar = await userApi.GetUserAvatarAsync();

            setAvatar(updatedAvatar);
            setSelectedFile(null);
            setPreviewUrl(null);

            if (inputRef.current) inputRef.current.value = "";        
    }
    catch {
        showError("Could not update your profile picture.");
    }
    finally {hideLoader(); setUpdatingPhoto(false);}
    }

    const handleRemoveAvatarAsync = async () => {
        try {
            showLoader();
            setIsRemoveDialogOpen(false);
            await userApi.RemoveAvatarAsync();
            setAvatar(null);

        }
        catch {
        }
        finally {
            hideLoader();
        }
    }


    return (
        <div className="flex flex-col lg:flex-row justify-center items-center gap-5">
            <Avatar className="size-32 lg:size-28 border">
                <AvatarImage alt="avatar" src={avatar?.url ?? " "}/>
                <AvatarFallback className="rounded-full ">
                    {<User className="size-20 text-slate-400"/>}
                </AvatarFallback>
            </Avatar>

            <div className="flex flex-col text-center lg:text-left gap-2">
                <p className="font-medium text-base">{`Profile Picture`}</p>
                <span className={
                    classNames(`text-xs`,!validPhoto ? "font-bold text-red-500" : "text-slate-400")}
                >
                    JPG, PNG or WebP. Maximum 5 MB.
                </span>

                <div className="flex flex-wrap justify-center lg:justify-start gap-2">
                <Button 
                    id="input" type="button"
                    // className="text-sm md:text-xs bg-white hover:bg-slate-500 hover:text-white text-slate-500 border border-slate-200 rounded-xl"  
                    className="text-xs bg-slate-500 hover:bg-slate-600 text-white rounded-2xl"  
                    onClick={() => inputRef.current?.click()}
                >
                    <UploadIcon className="h-4 w-4" />
                    Update Photo
                </Button>

                <Input  type="file" 
                        className="hidden" 
                        ref={inputRef} 
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleUpdatePhotoChanges}
                />

                {avatar?.url && <Button 
                    className="text-xs bg-white text-red-500 rounded-2xl border border-red-200 hover:bg-red-500 hover:text-white"
                    // onClick={handleRemoveAvatarAsync}
                    onClick={() => setIsRemoveDialogOpen(true)}
                    disabled= {!avatar?.url}
                >
                    <Trash2 className="h-4 w-4" />
                    Remove
                </Button>}

                {photoError && (
                    <p className="text-sm text-destructive" role="alert">
                        {photoError}
                    </p>
                )}
            </div>
            </div>

            {
                isRemoveDialogOpen && <DeleteConfirmationDialog
                open={isRemoveDialogOpen}
                onOpenChange={setIsRemoveDialogOpen}
                title="Remove profile picture?"
                description="Your current profile picture will be removed."
                actionLabel="Remove picture"
                onConfirm={handleRemoveAvatarAsync}
                />
            }
        </div>
    )
}